import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Pencil, Plus, Trash2, X } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useUser } from "../lib/UserContext";
import { usePermission } from "../lib/usePermission";
import {
  buildQuickMessageInsertPayload,
  buildQuickMessageUpdatePayload,
  normalizeQuickMessageShortcut,
} from "../lib/quickMessages.mjs";

type QuickMessage = {
  id?: string;
  praticien_id: string;
  display_name: string;
  shortcut: string;
  template_body: string;
};

const emptyQuickMessage = (clinicId: string): QuickMessage => ({
  praticien_id: clinicId,
  display_name: "",
  shortcut: "",
  template_body: "",
});

export default function ClinicQuickMessages({ embedded = false }: { embedded?: boolean }) {
  const user = useUser();
  const canManageTemplates = usePermission("manage_templates");
  const [quickMessages, setQuickMessages] = useState<QuickMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [editing, setEditing] = useState<QuickMessage | null>(null);
  const [saving, setSaving] = useState(false);

  async function loadQuickMessages() {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("clinic_templates")
      .select("id, praticien_id, display_name, shortcut, template_body")
      .eq("praticien_id", user.id)
      .eq("template_kind", "quick_message")
      .order("display_name");

    if (error) {
      toast.error("Failed to load quick messages");
      setQuickMessages([]);
    } else {
      setQuickMessages((data ?? []) as QuickMessage[]);
    }
    setLoading(false);
  }

  useEffect(() => {
    loadQuickMessages();
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user === undefined) {
    return <div className="p-6 text-gray-500">Loading quick messages...</div>;
  }

  if (user === null) {
    return <Navigate to="/login" replace />;
  }

  if (user.role === "admin" || !canManageTemplates) {
    return <Navigate to="/conversations" replace />;
  }

  function openNew() {
    setEditing(emptyQuickMessage(user.id));
    setPanelOpen(true);
  }

  function openEdit(row: QuickMessage) {
    setEditing({
      ...emptyQuickMessage(user.id),
      ...row,
      shortcut: row.shortcut ?? "",
      template_body: row.template_body ?? "",
      display_name: row.display_name ?? "",
    });
    setPanelOpen(true);
  }

  function closePanel() {
    setPanelOpen(false);
    setEditing(null);
  }

  async function handleSave() {
    if (!editing) return;
    const displayName = editing.display_name.trim();
    const shortcut = normalizeQuickMessageShortcut(editing.shortcut);
    const body = editing.template_body.trim();

    if (!displayName) {
      toast.error("Display name is required");
      return;
    }
    if (!shortcut) {
      toast.error("Shortcut is required");
      return;
    }
    if (!body) {
      toast.error("Message body is required");
      return;
    }

    setSaving(true);
    const { error } = editing.id
      ? await supabase
          .from("clinic_templates")
          .update(buildQuickMessageUpdatePayload(editing))
          .eq("id", editing.id)
          .eq("praticien_id", user.id)
          .eq("template_kind", "quick_message")
      : await supabase
          .from("clinic_templates")
          .insert(buildQuickMessageInsertPayload(user.id, editing));

    if (error) {
      toast.error("Error: " + error.message);
    } else {
      toast.success(editing.id ? "Quick message updated" : "Quick message created");
      closePanel();
      loadQuickMessages();
    }
    setSaving(false);
  }

  async function handleDelete(row: QuickMessage) {
    if (!row.id || !window.confirm(`Delete "${row.display_name}"?`)) return;
    const { error } = await supabase
      .from("clinic_templates")
      .delete()
      .eq("id", row.id)
      .eq("praticien_id", user.id)
      .eq("template_kind", "quick_message");

    if (error) {
      toast.error("Delete failed");
    } else {
      toast.success("Quick message deleted");
      loadQuickMessages();
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        {!embedded && (
          <div>
            <h1 className="text-2xl font-semibold text-slate-800">Quick messages</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Manage shortcuts your team can insert in patient conversations with /shortcut.
            </p>
          </div>
        )}
        <button
          onClick={openNew}
          className="flex items-center gap-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
        >
          <Plus size={16} />
          New quick message
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-500">Loading...</p>
      ) : quickMessages.length === 0 ? (
        <div className="text-center py-16 text-slate-400 border border-dashed border-slate-200 rounded-xl bg-white">
          <p className="text-base font-medium">No quick messages</p>
          <p className="text-sm mt-1">Create a shortcut your team can use from the composer.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-4 py-3 font-medium text-slate-600">Display name</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Shortcut</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600">Message body</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {quickMessages.map((row, i) => (
                <tr
                  key={row.id}
                  className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                    i === quickMessages.length - 1 ? "border-b-0" : ""
                  }`}
                >
                  <td className="px-4 py-3 font-medium text-slate-800">{row.display_name}</td>
                  <td className="px-4 py-3 text-slate-500 font-mono text-xs">/{row.shortcut}</td>
                  <td className="px-4 py-3 max-w-xl">
                    <p className="text-xs text-slate-500 whitespace-pre-wrap line-clamp-3" title={row.template_body}>
                      {row.template_body || "-"}
                    </p>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <button
                        onClick={() => openEdit(row)}
                        className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                        title="Edit"
                      >
                        <Pencil size={15} />
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                        title="Delete"
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {panelOpen && editing && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={closePanel} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-base font-semibold text-slate-800">
                {editing.id ? "Edit quick message" : "New quick message"}
              </h2>
              <button
                onClick={closePanel}
                className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
              >
                <X size={18} />
              </button>
            </div>

            <div className="flex-1 px-6 py-5 space-y-5">
              <Field label="Display name" required hint="Label shown in the quick messages table">
                <input
                  type="text"
                  value={editing.display_name}
                  onChange={(e) => setEditing((prev) => prev ? { ...prev, display_name: e.target.value } : prev)}
                  placeholder="e.g. Taking over the conversation"
                  className={inputClass}
                />
              </Field>

              <Field label="Shortcut" required hint="Type /shortcut in the composer to insert this message.">
                <div className="flex items-center rounded-lg border border-slate-200 bg-white focus-within:ring-2 focus-within:ring-slate-300">
                  <span className="px-3 text-sm font-mono text-slate-400">/</span>
                  <input
                    type="text"
                    value={editing.shortcut}
                    onChange={(e) =>
                      setEditing((prev) =>
                        prev
                          ? { ...prev, shortcut: normalizeQuickMessageShortcut(e.target.value) }
                          : prev
                      )
                    }
                    placeholder="taking_over"
                    className="w-full rounded-r-lg px-0 py-2 pr-3 text-sm text-slate-800 placeholder-slate-400 focus:outline-none font-mono"
                  />
                </div>
              </Field>

              <Field label="Message body" required hint="Inserted directly into the draft and remains editable before sending.">
                <textarea
                  value={editing.template_body}
                  onChange={(e) => setEditing((prev) => prev ? { ...prev, template_body: e.target.value } : prev)}
                  rows={7}
                  placeholder="Hello, I am taking over your conversation. I will review your request and get back to you shortly."
                  className={`${inputClass} resize-none text-sm leading-relaxed`}
                />
              </Field>
            </div>

            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3">
              <button
                onClick={closePanel}
                className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-5 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {saving ? "Saving..." : editing.id ? "Update" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputClass =
  "w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 focus:border-transparent transition";

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="block text-sm font-medium text-slate-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {hint && <p className="text-xs text-slate-400 leading-snug">{hint}</p>}
      {children}
    </div>
  );
}
